import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '@/services';
import { useToast } from '../components/Toast';
import PINInput from '../components/PINInput';

const ValidateAccount = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { error, success } = useToast();
  const [loading, setLoading] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [phone, setPhone] = useState('');
  const [defaultPin, setDefaultPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState(1); // 1: phone + default PIN, 2: new PIN

  useEffect(() => {
    loadAccountInfo();
  }, [token]);

  const loadAccountInfo = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getValidateAccountInfo(token);
      setAccountInfo(response.data);
    } catch (err) {
      error('Token de validation invalide ou expiré');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    
    if (!phone || !defaultPin) {
      error('Veuillez remplir tous les champs');
      return;
    }

    if (defaultPin.length !== 6) {
      error('Le PIN par défaut doit contenir 6 chiffres');
      return;
    }

    // Passer à l'étape 2 (la vérification du PIN se fera lors de la soumission finale)
    setStep(2);
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    
    if (!newPin || !confirmPin) {
      error('Veuillez remplir tous les champs');
      return;
    }

    if (newPin.length !== 6) {
      error('Le nouveau PIN doit contenir 6 chiffres');
      return;
    }

    if (newPin !== confirmPin) {
      error('Les PIN ne correspondent pas');
      return;
    }

    try {
      setLoading(true);
      await authAPI.validateAccount({
        token,
        phone,
        default_pin: defaultPin,
        new_pin: newPin
      });
      
      success('Compte validé avec succès ! Vous pouvez maintenant vous connecter.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la validation du compte');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !accountInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-entrea-panel via-white to-entrea-surface flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!accountInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-entrea-panel via-white to-entrea-surface flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Token invalide</h1>
          <p className="text-gray-600 mb-6">Le lien de validation est invalide ou a expiré.</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-4 py-3 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy font-medium"
          >
            Aller à la page de connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-entrea-panel via-white to-entrea-surface flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="text-entrea-royal text-5xl mb-4">✅</div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Validation de votre compte
          </h1>
          <p className="text-gray-600">
            {step === 1 
              ? 'Confirmez votre numéro de téléphone et entrez le PIN par défaut'
              : 'Créez un nouveau PIN pour votre compte'}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleStep1Submit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de téléphone
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^0-9+]/g, ''))}
                placeholder={accountInfo.phone || 'Entrez votre numéro'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky text-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PIN par défaut
              </label>
              <PINInput
                value={defaultPin}
                onChange={setDefaultPin}
                length={6}
              />
              <p className="mt-2 text-xs text-gray-500">
                Entrez le PIN par défaut qui vous a été communiqué
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Vérification...' : 'Continuer'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleStep2Submit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nouveau PIN (6 chiffres)
              </label>
              <PINInput
                value={newPin}
                onChange={setNewPin}
                length={6}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer le nouveau PIN
              </label>
              <PINInput
                value={confirmPin}
                onChange={setConfirmPin}
                length={6}
              />
            </div>

            {newPin && confirmPin && newPin !== confirmPin && (
              <div className="text-red-600 text-sm">
                Les PIN ne correspondent pas
              </div>
            )}

            <button
              type="submit"
              disabled={loading || newPin.length !== 6 || confirmPin.length !== 6 || newPin !== confirmPin}
              className="w-full px-4 py-3 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Validation...' : 'Valider mon compte'}
            </button>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Retour
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ValidateAccount;

